/**
 * Ratings & Reviews Page
 * ======================
 * صفحة التقييمات والتعليقات ومؤشر الرضا - from database
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { Modal } from '../../../lib/ui/Modal';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { ratingsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';
import './RatingsPage.css';

const Icons = {
  star: <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  starEmpty: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  eyeOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  flag: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  smile: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>,
  frown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>,
  trendUp: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  trendDown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>,
};

const RatingsPage = () => {
  const toast = useToast();
  const { confirm } = useDialog();

  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRating, setSelectedRating] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ratingsData, statsData] = await Promise.all([
        ratingsService.getAll().catch(() => ({ items: [] })),
        ratingsService.getStats().catch(() => ({})),
      ]);

      setRatings(ratingsData.items || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (rating, newStatus) => {
    try {
      await ratingsService.updateStatus(rating.id, newStatus);
      toast.success('تم تحديث حالة التقييم');
      loadData();
    } catch (error) {
      toast.error('فشل في تحديث حالة التقييم');
    }
  };

  const handleDelete = async (rating) => {
    if (!await confirm({ title: 'حذف التقييم', message: 'هل أنت متأكد من حذف هذا التقييم؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await ratingsService.delete(rating.id);
      toast.success('تم حذف التقييم');
      loadData();
    } catch (error) {
      toast.error('فشل في حذف التقييم');
    }
  };

  const getTypeLabel = (type) => {
    const labels = { driver: 'سائق', supplier: 'مورد', order: 'طلب', service: 'خدمة' };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = { published: 'منشور', hidden: 'مخفي', flagged: 'مبلغ عنه' };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`watrly-ratings__star ${i <= rating ? 'watrly-ratings__star--filled' : ''}`}>
          {i <= rating ? Icons.star : Icons.starEmpty}
        </span>
      );
    }
    return stars;
  };

  const filteredRatings = useMemo(() => {
    return ratings.filter(r => {
      const matchesSearch = r.comment?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || r.entity_type === typeFilter;
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [ratings, searchQuery, typeFilter, statusFilter]);

  const typeOptions = [
    { value: 'all', label: 'جميع الأنواع' },
    { value: 'driver', label: 'سائقين' },
    { value: 'supplier', label: 'موردين' },
    { value: 'order', label: 'طلبات' },
    { value: 'service', label: 'خدمات' },
  ];

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'published', label: 'منشور' },
    { value: 'hidden', label: 'مخفي' },
    { value: 'flagged', label: 'مبلغ عنه' },
  ];

  const columns = [
    { 
      key: 'rating', 
      title: 'التقييم', 
      render: (val) => (
        <div className="watrly-ratings__stars">
          {renderStars(val)}
          <span className="watrly-ratings__rating-value">{val}</span>
        </div>
      )
    },
    { 
      key: 'entity_type', 
      title: 'النوع', 
      render: (val) => (
        <span className={`watrly-ratings__type watrly-ratings__type--${val}`}>
          {getTypeLabel(val)}
        </span>
      )
    },
    { 
      key: 'comment', 
      title: 'التعليق', 
      render: (val) => (
        <span className="watrly-ratings__comment">
          {val ? (val.length > 50 ? val.substring(0, 50) + '...' : val) : '-'}
          <small> - from database</small>
        </span>
      )
    },
    { key: 'created_at', title: 'التاريخ', render: (val) => formatDate(val) },
    { 
      key: 'status', 
      title: 'الحالة', 
      render: (val) => (
        <span className={`watrly-ratings__status watrly-ratings__status--${val}`}>
          {getStatusLabel(val)}
        </span>
      )
    },
    { 
      key: 'actions', 
      title: '', 
      sortable: false, 
      render: (_, row) => (
        <div className="watrly-ratings__actions">
          <button 
            className="watrly-ratings__action-btn"
            onClick={() => { setSelectedRating(row); setShowDetailModal(true); }}
            title="عرض التفاصيل"
          >
            {Icons.eye}
          </button>
          {row.status === 'published' ? (
            <button 
              className="watrly-ratings__action-btn watrly-ratings__action-btn--hide"
              onClick={() => handleUpdateStatus(row, 'hidden')}
              title="إخفاء"
            >
              {Icons.eyeOff}
            </button>
          ) : (
            <button 
              className="watrly-ratings__action-btn watrly-ratings__action-btn--show"
              onClick={() => handleUpdateStatus(row, 'published')}
              title="إظهار"
            >
              {Icons.eye}
            </button>
          )}
          <button 
            className="watrly-ratings__action-btn watrly-ratings__action-btn--flag"
            onClick={() => handleUpdateStatus(row, 'flagged')}
            title="إبلاغ"
          >
            {Icons.flag}
          </button>
          <button 
            className="watrly-ratings__action-btn watrly-ratings__action-btn--delete"
            onClick={() => handleDelete(row)}
            title="حذف"
          >
            {Icons.trash}
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="watrly-ratings">
      {/* Header */}
      <div className="watrly-ratings__header">
        <div>
          <h1 className="watrly-ratings__title">التقييمات والتعليقات</h1>
          <p className="watrly-ratings__subtitle">
            إدارة تقييمات العملاء ومؤشر الرضا • البيانات من قاعدة البيانات
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="watrly-ratings__stats">
        <div className="watrly-ratings__stat-card watrly-ratings__stat-card--satisfaction">
          <div className="watrly-ratings__stat-icon">{Icons.smile}</div>
          <div className="watrly-ratings__stat-content">
            <span className="watrly-ratings__stat-value">{stats.satisfaction_rate || 0}%</span>
            <span className="watrly-ratings__stat-label">نسبة الرضا</span>
          </div>
          <div className={`watrly-ratings__stat-trend ${stats.trend >= 0 ? 'watrly-ratings__stat-trend--up' : 'watrly-ratings__stat-trend--down'}`}>
            {stats.trend >= 0 ? Icons.trendUp : Icons.trendDown}
            {Math.abs(stats.trend || 0)}%
          </div>
        </div>

        <div className="watrly-ratings__stat-card">
          <div className="watrly-ratings__stat-icon">{Icons.star}</div>
          <div className="watrly-ratings__stat-content">
            <span className="watrly-ratings__stat-value">{stats.average_rating || 0}</span>
            <span className="watrly-ratings__stat-label">متوسط التقييم</span>
          </div>
        </div>

        <div className="watrly-ratings__stat-card watrly-ratings__stat-card--positive">
          <div className="watrly-ratings__stat-icon">{Icons.smile}</div>
          <div className="watrly-ratings__stat-content">
            <span className="watrly-ratings__stat-value">{stats.positive_count || 0}</span>
            <span className="watrly-ratings__stat-label">تقييم إيجابي</span>
          </div>
        </div>

        <div className="watrly-ratings__stat-card watrly-ratings__stat-card--negative">
          <div className="watrly-ratings__stat-icon">{Icons.frown}</div>
          <div className="watrly-ratings__stat-content">
            <span className="watrly-ratings__stat-value">{stats.negative_count || 0}</span>
            <span className="watrly-ratings__stat-label">تقييم سلبي</span>
          </div>
        </div>
      </div>

      {/* Rating Distribution */}
      {stats.distribution && Object.keys(stats.distribution).length > 0 && (
        <Card padding="md" className="watrly-ratings__distribution-card">
          <h3>توزيع التقييمات</h3>
          <div className="watrly-ratings__distribution">
            {[5, 4, 3, 2, 1].map(star => (
              <div key={star} className="watrly-ratings__distribution-row">
                <span className="watrly-ratings__distribution-label">
                  {star} {Icons.star}
                </span>
                <div className="watrly-ratings__distribution-bar">
                  <div 
                    className="watrly-ratings__distribution-fill"
                    style={{ width: `${((stats.distribution[star] || 0) / stats.total) * 100}%` }}
                  />
                </div>
                <span className="watrly-ratings__distribution-count">
                  {stats.distribution[star] || 0}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card padding="md" className="watrly-ratings__filters-card">
        <div className="watrly-ratings__filters">
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="بحث في التعليقات..."
            className="watrly-ratings__search"
          />
          <div className="watrly-ratings__filter-group">
            <Dropdown
              options={typeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
              placeholder="النوع"
            />
            <Dropdown
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="الحالة"
            />
          </div>
        </div>
      </Card>

      {/* Results */}
      <div className="watrly-ratings__results-info">
        عرض {filteredRatings.length} من {ratings.length} تقييم
      </div>

      {/* Table */}
      <Card padding="none" className="watrly-ratings__table-card">
        <Table
          columns={columns}
          data={filteredRatings}
          loading={loading}
          emptyMessage="لا يوجد تقييمات حالياً"
          hoverable
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="تفاصيل التقييم"
        size="md"
      >
        {selectedRating && (
          <div className="watrly-ratings__detail">
            <div className="watrly-ratings__detail-rating">
              {renderStars(selectedRating.rating)}
              <span className="watrly-ratings__detail-value">{selectedRating.rating}/5</span>
            </div>
            <div className="watrly-ratings__detail-row">
              <span className="watrly-ratings__detail-label">النوع:</span>
              <span className="watrly-ratings__detail-value">{getTypeLabel(selectedRating.entity_type)}</span>
            </div>
            <div className="watrly-ratings__detail-row">
              <span className="watrly-ratings__detail-label">التاريخ:</span>
              <span className="watrly-ratings__detail-value">{formatDate(selectedRating.created_at)}</span>
            </div>
            <div className="watrly-ratings__detail-row">
              <span className="watrly-ratings__detail-label">الحالة:</span>
              <span className={`watrly-ratings__status watrly-ratings__status--${selectedRating.status}`}>
                {getStatusLabel(selectedRating.status)}
              </span>
            </div>
            {selectedRating.comment && (
              <div className="watrly-ratings__detail-comment">
                <span className="watrly-ratings__detail-label">التعليق:</span>
                <p>{selectedRating.comment}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RatingsPage;
